import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { isRealAthleteId } from './useSetLogs'

export interface DayCompletion {
  week: number
  day: number
  completed_at: string
}

/** Key for a single day within a program. */
export const dayKey = (week: number, day: number) => `${week}:${day}`

/**
 * Every day this athlete has finished, for the given program. Returned as a Set
 * of "week:day" keys plus the furthest week reached, so the coach can see real
 * progress rather than guessing from current_week.
 */
export function useDayCompletions(athleteId: string, programId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['day-completions', athleteId, programId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId) && Boolean(programId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('day_completions')
        .select('week,day,completed_at')
        .eq('athlete_id', athleteId)
        .eq('program_id', programId!)
      if (error) throw error
      const rows = (data ?? []) as DayCompletion[]
      const done = new Set(rows.map((r) => dayKey(r.week, r.day)))
      const lastWeek = rows.length ? Math.max(...rows.map((r) => r.week)) : null
      const lastAt = rows.length
        ? rows.map((r) => r.completed_at).sort().slice(-1)[0]
        : null
      return { done, rows, lastWeek, lastAt }
    },
  })
}

/** Mark a day complete, or clear it if it was already marked. */
export function useToggleDayComplete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: {
      athleteId: string
      programId: string
      week: number
      day: number
      complete: boolean
    }) => {
      if (!isRealAthleteId(v.athleteId)) return
      if (v.complete) {
        const { data: session } = await supabase.auth.getSession()
        const { error } = await supabase.from('day_completions').upsert(
          {
            athlete_id: v.athleteId,
            program_id: v.programId,
            week: v.week,
            day: v.day,
            completed_at: new Date().toISOString(),
            completed_by: session.session?.user.id ?? null,
          },
          { onConflict: 'athlete_id,program_id,week,day' }
        )
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('day_completions')
          .delete()
          .eq('athlete_id', v.athleteId)
          .eq('program_id', v.programId)
          .eq('week', v.week)
          .eq('day', v.day)
        if (error) throw error
      }
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['day-completions', v.athleteId, v.programId] })
    },
  })
}
